import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../sections/Navigation';
import FooterSection from '../sections/FooterSection';
import './ResearchPage.css';

const ResearchPage = () => {
  const [language, setLanguage] = useState('en');
  const navigate = useNavigate();

  const content = {
    en: {
      pageTitle: 'Research & Development',
      pageSubtitle: 'EU-funded research projects advancing underwater autonomy technology',
      sectionLabel: 'Active Projects',
      projectNumber: 'Project No.',
      viewProject: 'View Project',
      start: 'Start',
      end: 'End',
      role: 'Role',
      active: 'Active',
      projects: [
        {
          id: 'dpkc-pipeline',
          label: 'DPKC Research Project',
          number: '2.2.1.3.i.0/2/24/A/CFLA/001',
          title: 'Autonomous Underwater Pipeline Tracking and Scanning System Using Multi-beam Sonar',
          description: 'Development of a low-readiness prototype for an underwater pipeline identification and tracking system — enabling autonomous detection, tracking, and damage assessment of underwater pipelines using multi-beam sonar data.',
          statusKey: 'active',
          start: '01.02.2025',
          end: '01.04.2026',
          role: 'Sole Implementer',
        },
        {
          id: 'cfla18-auv',
          label: 'CFLA Research Project',
          number: '1.2.1.1/3/25/A/018',
          title: 'Scalable Dual-Use Autonomous Underwater Drone System with Integrated Data Infrastructure and STANAG-Compliant Electromagnetic Profile',
          description: 'Development of a scalable, dual-use autonomous underwater drone (AUV) system with integrated data infrastructure and STANAG-compliant electromagnetic profile — for civilian infrastructure monitoring and military applications, targeting TRL8.',
          statusKey: 'active',
          start: '01.09.2025',
          end: '31.08.2027',
          role: 'Project Partner',
        },
        {
          id: 'cfla-lars',
          label: 'CFLA Research Project',
          number: '1.2.1.2.i.2/1/24/A/CFLA/007',
          title: 'Development of an Autonomous LARS System for USV–AUV Integration for Underwater Infrastructure Inspection',
          description: 'Development of an autonomous LARS (Launch and Recovery System) enabling seamless USV–AUV integration for underwater infrastructure inspection — autonomous AUV deployment, positioning, and recovery in dynamic marine conditions, delivering high-resolution 3D data while reducing reliance on crewed vessels.',
          statusKey: 'active',
          start: '01.04.2026',
          end: '31.03.2027',
          role: 'Partner',
        },
      ],
    },
    lv: {
      pageTitle: 'Pētniecība un Attīstība',
      pageSubtitle: 'ES finansēti pētniecības projekti, kas attīsta zemūdens autonomijas tehnoloģijas',
      sectionLabel: 'Aktīvie projekti',
      projectNumber: 'Projekta Nr.',
      viewProject: 'Skatīt projektu',
      start: 'Sākums',
      end: 'Beigas',
      role: 'Loma',
      active: 'Aktīvs',
      projects: [
        {
          id: 'dpkc-pipeline',
          label: 'DPKC Pētījuma Projekts',
          number: '2.2.1.3.i.0/2/24/A/CFLA/001',
          title: 'Autonoma zemūdens cauruļvadu izsekošanas un skenēšanas sistēma izmantojot daudzstaru sonāru',
          description: 'Zemas gatavības prototipa izstrāde zemūdens cauruļvadu identificēšanas un izsekošanas sistēmai — ļaujot autonomi atklāt, izsekot un novērtēt zemūdens cauruļvadu bojājumus, izmantojot daudzstaru sonāra datus.',
          statusKey: 'active',
          start: '01.02.2025',
          end: '01.04.2026',
          role: 'Vienīgais īstenotājs',
        },
        {
          id: 'cfla18-auv',
          label: 'CFLA Pētījuma Projekts',
          number: '1.2.1.1/3/25/A/018',
          title: 'Mērogojama duāla lietojuma autonomā zemūdens drona sistēma ar integrētu datu infrastruktūru un STANAG atbilstošu elektromagnētisko profilu',
          description: 'Mērogojamas, divējādas lietojamības autonomā zemūdens drona (AZD) sistēmas izstrāde ar integrētu datu infrastruktūru un STANAG atbilstošu elektromagnētisko profilu — civilai infrastruktūras uzraudzībai un militāriem pielietojumiem, mērķis TRL8.',
          statusKey: 'active',
          start: '01.09.2025',
          end: '31.08.2027',
          role: 'Projekta partneris',
        },
        {
          id: 'cfla-lars',
          label: 'CFLA Pētījuma Projekts',
          number: '1.2.1.2.i.2/1/24/A/CFLA/007',
          title: 'Autonomas LARS sistēmas izstrāde USV–AUV integrācijai zemūdens infrastruktūras apsekošanai',
          description: 'Autonomas LARS (palaišanas un savākšanas sistēmas) izstrāde, kas nodrošina efektīvu USV–AUV integrāciju zemūdens infrastruktūras apsekošanai — AUV autonoma palaišana, pozicionēšana un savākšana dinamiskos jūras apstākļos, ar augstas izšķirtspējas 3D datu ieguvi, samazinot nepieciešamību pēc apkalpotiem kuģiem.',
          statusKey: 'active',
          start: '01.04.2026',
          end: '31.03.2027',
          role: 'Partneris',
        },
      ],
    },
  };

  const t = content[language];

  return (
    <div className="research-page">
      <Navigation />

      <main className="research-main">
        <div className="container">
          {/* Language Toggle */}
          <div className="language-toggle">
            <button
              type="button"
              className={`lang-btn ${language === 'en' ? 'active' : ''}`}
              onClick={() => setLanguage('en')}
            >
              EN
            </button>
            <button
              type="button"
              className={`lang-btn ${language === 'lv' ? 'active' : ''}`}
              onClick={() => setLanguage('lv')}
            >
              LV
            </button>
          </div>

          {/* Page Header */}
          <div className="research-header">
            <span className="section-label">{t.sectionLabel}</span>
            <h1 className="research-title">{t.pageTitle}</h1>
            <p className="research-subtitle">{t.pageSubtitle}</p>
          </div>

          {/* Project List */}
          <div className="project-list">
            {t.projects.map((project) => (
              <div
                className="project-list-card"
                key={project.id}
                onClick={() => navigate(`/research/${project.id}?lang=${language}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/research/${project.id}?lang=${language}`)}
              >
                <div className="project-list-card-header">
                  <span className="project-list-label">{project.label}</span>
                  <span className={`project-status-badge ${project.statusKey}`}>
                    {t[project.statusKey]}
                  </span>
                </div>

                <div className="project-list-number">
                  <span className="label">{t.projectNumber}</span>
                  <span className="value">{project.number}</span>
                </div>

                <h2 className="project-list-title">{project.title}</h2>
                <p className="project-list-description">{project.description}</p>

                <div className="project-list-meta">
                  <div className="meta-item">
                    <span className="meta-label">{t.start}</span>
                    <span className="meta-value">{project.start}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">{t.end}</span>
                    <span className="meta-value">{project.end}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">{t.role}</span>
                    <span className="meta-value">{project.role}</span>
                  </div>
                </div>

                <div className="project-list-card-footer">
                  <span className="view-project-btn">
                    {t.viewProject}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12,5 19,12 12,19"></polyline>
                    </svg>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default ResearchPage;
